/**
 * 録音用の大きな mic ボタン。録音中はパルス animation で進行を表現。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 録音画面の hero インタラクション。idle / recording / processing の 3 state を表現
 * @graph-connects none
 */

import type { ReactNode } from 'react';

interface MicButtonProps {
  state: 'idle' | 'recording' | 'processing';
  onClick: () => void;
  ariaLabel: string;
  disabled?: boolean;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business mic ボタン本体。録音中は pulse-ring アニメで視覚的フィードバック
 * @graph-connects none
 */
export function MicButton({ state, onClick, ariaLabel, disabled }: MicButtonProps): ReactNode {
  const isRecording = state === 'recording';
  const isProcessing = state === 'processing';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={(disabled ?? false) || isProcessing}
      aria-label={ariaLabel}
      aria-pressed={isRecording}
      className={[
        'relative inline-flex h-24 w-24 items-center justify-center rounded-full',
        'transition-all duration-[var(--duration-base)] ease-[var(--ease-out)]',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        isRecording
          ? 'bg-[var(--color-danger)] text-white shadow-[0_16px_48px_-12px_rgba(248,113,113,0.55)] scale-105'
          : 'bg-[var(--color-accent)] text-[var(--color-bg-base)] shadow-[0_16px_48px_-12px_var(--color-accent-glow)] hover:scale-105 active:scale-100',
      ].join(' ')}
    >
      {isRecording ? (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-[var(--color-danger)] opacity-60"
          style={{ animation: 'pulse-ring 1.4s var(--ease-out) infinite' }}
        />
      ) : null}
      <span aria-hidden className="relative z-10">
        {isProcessing ? <ProcessingIcon /> : isRecording ? <StopIcon /> : <MicIcon />}
      </span>
    </button>
  );
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business idle 状態の録音ボタン用マイクアイコン SVG
 * @graph-connects none
 */
function MicIcon(): ReactNode {
  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="3" width="6" height="12" rx="3" fill="currentColor" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
    </svg>
  );
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business recording 状態の録音ボタン用停止アイコン SVG
 * @graph-connects none
 */
function StopIcon(): ReactNode {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business processing 状態の録音ボタン用スピナーアイコン SVG
 * @graph-connects none
 */
function ProcessingIcon(): ReactNode {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="animate-spin"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
